import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Award,
  Loader2,
  RefreshCw,
  Trophy,
  Users,
  Zap
} from 'lucide-react';
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';

interface ClassItem {
  Klasse: string;
  anzahl?: number;
}

interface StudentRecord {
  sophomorixUnid: string;
  Name: string;
  Vorname: string;
  Klasse: string;
  Foto?: string;
}

interface SnakeStudent extends StudentRecord {
  fullName: string;
}

interface Position {
  x: number;
  y: number;
}

interface FoodItem {
  id: string;
  position: Position;
  student: SnakeStudent;
}

interface HighscoreEntry {
  username: string;
  score: number;
  maxLength?: number;
  durationSeconds?: number;
  updatedAt?: string;
  createdAt?: string;
  classes?: string[];
}

interface HighscorePayload {
  score: number;
  maxLength: number;
  durationSeconds: number;
  classes: string[];
}

const GRID_COLUMNS = 12;
const GRID_ROWS = 8;
const TICK_INTERVAL = 600;
const FOOD_TARGET_COUNT = 3;
const MIN_TILE_SIZE = 78;
const INITIAL_SNAKE: Position[] = [
  { x: Math.floor(GRID_COLUMNS / 2), y: Math.floor(GRID_ROWS / 2) },
  { x: Math.floor(GRID_COLUMNS / 2) - 1, y: Math.floor(GRID_ROWS / 2) },
  { x: Math.floor(GRID_COLUMNS / 2) - 2, y: Math.floor(GRID_ROWS / 2) }
];

const getHighscoreTimestamp = (entry: HighscoreEntry) => entry.updatedAt || entry.createdAt || '';

type GamePhase = 'setup' | 'loading' | 'ready' | 'playing' | 'paused' | 'finished';
type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
type GameOverReason = 'collision' | 'wrongFood';

export default function NamenLernenSnake() {
  const { user } = useAuth();
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [gamePhase, setGamePhase] = useState<GamePhase>('setup');
  const [snake, setSnake] = useState<Position[]>(INITIAL_SNAKE);
  const snakeRef = useRef<Position[]>(INITIAL_SNAKE);
  const [direction, setDirection] = useState<Direction>('RIGHT');
  const directionRef = useRef<Direction>('RIGHT');
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const foodItemsRef = useRef<FoodItem[]>([]);
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string | null>>({});
  const [targetFoodId, setTargetFoodId] = useState<string>('');
  const [studentPool, setStudentPool] = useState<SnakeStudent[]>([]);
  const [score, setScore] = useState(0);
  const [maxLength, setMaxLength] = useState(INITIAL_SNAKE.length);
  const [lastDuration, setLastDuration] = useState(0);
  const startTimestampRef = useRef<number | null>(null);
  const [gameOverReason, setGameOverReason] = useState<GameOverReason>('collision');
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const gamePhaseRef = useRef<GamePhase>('setup');
  const boardWrapperRef = useRef<HTMLDivElement>(null);
  const [tileSize, setTileSize] = useState<number>(MIN_TILE_SIZE);
  const classesInitializedRef = useRef(false);

  const { data: classesResponse, isLoading: classesLoading } = useQuery(
    ['namen-lernen-classes'],
    async () => {
      const response = await api.get('/students/classes');
      return response.data?.data || [];
    },
    {
      onError: () => toast.error('Klassen konnten nicht geladen werden')
    }
  );

const normalizedSelectedClasses = useMemo(() => Array.from(new Set(selectedClasses)).filter(Boolean).sort(), [selectedClasses]);

  const classes: ClassItem[] = useMemo(() => {
    if (!classesResponse) return [];
    return classesResponse
      .map((item: any) => ({ Klasse: item.Klasse || item.klasse || item.name, anzahl: item.anzahl || item.Anzahl }))
      .filter((item: ClassItem) => !!item.Klasse)
      .sort((a: ClassItem, b: ClassItem) => a.Klasse.localeCompare(b.Klasse, 'de-DE'));
  }, [classesResponse]);

  const { data: highscoreResponse, isLoading: highscoreLoading, refetch: refetchHighscores } = useQuery(
    ['namen-lernen-snake-highscores'],
    async () => {
      try {
        const response = await api.get('/namen-lernen/snake/highscores');
        return response.data?.data || response.data;
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return [];
        }
        throw error;
      }
    },
    {
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        if (error?.response?.status !== 404) {
          toast.error('Highscores konnten nicht geladen werden');
        }
      }
    }
  );

  const highscoresFromServer: HighscoreEntry[] = useMemo(() => {
    if (!highscoreResponse) return [];
    if (Array.isArray(highscoreResponse)) return highscoreResponse;
    if (Array.isArray(highscoreResponse?.highscores)) return highscoreResponse.highscores;
    return [];
  }, [highscoreResponse]);

  const [localHighscores, setLocalHighscores] = useState<HighscoreEntry[]>([]);

  const mergedHighscores: HighscoreEntry[] = useMemo(() => {
    const map = new Map<string, HighscoreEntry>();

    const absorb = (entries: HighscoreEntry[]) => {
      entries.forEach(entry => {
        if (!entry) return;
        const key = (entry.username || '').toLowerCase();
        const current = map.get(key);
        if (!current || (entry.score ?? 0) > (current.score ?? 0)) {
          map.set(key, entry);
        }
      });
    };

    absorb(highscoresFromServer);
    absorb(localHighscores);

    return Array.from(map.values()).sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }, [highscoresFromServer, localHighscores]);

  const userBestScore: HighscoreEntry | null = useMemo(() => {
    const username = user?.username?.toLowerCase();
    if (!username) return null;
    return mergedHighscores.find(entry => entry.username?.toLowerCase() === username) ?? null;
  }, [mergedHighscores, user?.username]);

  const submitHighscoreMutation = useMutation<void, unknown, HighscorePayload>(
    ['namen-lernen-snake-highscore'],
    async (payload: HighscorePayload) => {
      await api.post('/namen-lernen/snake/highscore', payload);
    },
    {
      onSuccess: (_, variables) => {
        if (user?.username) {
          const entry: HighscoreEntry = {
            username: user.username,
            score: variables.score,
            maxLength: variables.maxLength,
            durationSeconds: variables.durationSeconds,
            updatedAt: new Date().toISOString(),
            classes: variables.classes
          };
          setLocalHighscores(prev => {
            const filtered = prev.filter(i => i.username?.toLowerCase() !== entry.username.toLowerCase());
            return [...filtered, entry];
          });
        }
        toast.success('Highscore gespeichert!');
        refetchHighscores();
      },
      onError: () => {
        if (user?.username) {
          const entry: HighscoreEntry = {
            username: user.username,
            score,
            maxLength,
            durationSeconds: lastDuration,
            updatedAt: new Date().toISOString(),
            classes: selectedClasses
          };
          setLocalHighscores(prev => {
            const filtered = prev.filter(i => i.username?.toLowerCase() !== entry.username.toLowerCase());
            return [...filtered, entry];
          });
        }
        toast('Highscore lokal gespeichert (Server nicht erreichbar)', { icon: '💾' });
      }
    }
  );

  useEffect(() => {
    if (!classesInitializedRef.current && classes.length > 0) {
      setSelectedClasses(classes.map(item => item.Klasse));
      classesInitializedRef.current = true;
    }
  }, [classes]);

  useEffect(() => {
    gamePhaseRef.current = gamePhase;
  }, [gamePhase]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key;
      
      // Space-Taste für Start/Pause
      if (key === ' ') {
        event.preventDefault();
        const currentPhase = gamePhaseRef.current;
        if (currentPhase === 'playing') {
          setGamePhase('paused');
        } else if (currentPhase === 'paused') {
          setGamePhase('playing');
        }
        return;
      }

      // Pfeiltasten nur im Spiel verwenden
      if (gamePhaseRef.current !== 'playing') return;
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;
      event.preventDefault();

      const currentDirection = directionRef.current;
      let nextDirection: Direction = currentDirection;

      if (key === 'ArrowUp' && currentDirection !== 'DOWN') nextDirection = 'UP';
      if (key === 'ArrowDown' && currentDirection !== 'UP') nextDirection = 'DOWN';
      if (key === 'ArrowLeft' && currentDirection !== 'RIGHT') nextDirection = 'LEFT';
      if (key === 'ArrowRight' && currentDirection !== 'LEFT') nextDirection = 'RIGHT';

      if (nextDirection !== currentDirection) {
        directionRef.current = nextDirection;
        setDirection(nextDirection);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getRandomEmptyCell = useCallback((occupied: Position[]): Position => {
    const occupiedKey = new Set(occupied.map(pos => `${pos.x}-${pos.y}`));
    let attempt = 0;
    while (attempt < 1000) {
      const candidate = {
        x: Math.floor(Math.random() * GRID_COLUMNS),
        y: Math.floor(Math.random() * GRID_ROWS)
      };
      if (!occupiedKey.has(`${candidate.x}-${candidate.y}`)) {
        return candidate;
      }
      attempt += 1;
    }
    return { x: 1, y: 1 };
  }, []);

  const buildFoodItem = useCallback((existingPositions: Position[], existingFood: FoodItem[], pool: SnakeStudent[]): FoodItem | null => {
    const source = pool.length ? pool : studentPool;
    if (source.length === 0) {
      return null;
    }
    
    // Filtere Schüler aus, die bereits als Food-Items existieren
    const usedStudentIds = new Set(existingFood.map(item => item.student.sophomorixUnid));
    const availableStudents = source.filter(student => !usedStudentIds.has(student.sophomorixUnid));
    
    if (availableStudents.length === 0) {
      return null;
    }
    
    const candidateStudent = availableStudents[Math.floor(Math.random() * availableStudents.length)];
    const occupied = [
      ...existingPositions,
      ...existingFood.map(item => item.position)
    ];
    const position = getRandomEmptyCell(occupied);
    return {
      id: `${candidateStudent.sophomorixUnid}-${Date.now()}-${Math.random()}`,
      position,
      student: candidateStudent
    };
  }, [getRandomEmptyCell, studentPool]);

  const pickNewTarget = useCallback((items: FoodItem[]) => {
    if (items.length === 0) {
      setTargetFoodId('');
      return;
    }
    const chosen = items[Math.floor(Math.random() * items.length)];
    setTargetFoodId(chosen.id);
  }, []);

  const resetGameState = useCallback(() => {
    snakeRef.current = INITIAL_SNAKE;
    setSnake(INITIAL_SNAKE);
    directionRef.current = 'RIGHT';
    setDirection('RIGHT');
    setFoodItems([]);
    foodItemsRef.current = [];
    setTargetFoodId('');
    setScore(0);
    setMaxLength(INITIAL_SNAKE.length);
    setLastDuration(0);
    setGameOverReason('collision');
    startTimestampRef.current = null;
  }, []);

  const finishGame = useCallback((reason: GameOverReason) => {
    const endTimestamp = Date.now();
    const durationSeconds = startTimestampRef.current ? Math.round((endTimestamp - startTimestampRef.current) / 1000) : 0;
    setLastDuration(durationSeconds);
    setGamePhase('finished');
    setGameOverReason(reason);
    setIsSubmittingScore(true);
    submitHighscoreMutation.mutate({
      score,
      maxLength,
      durationSeconds,
      classes: selectedClasses
    });
  }, [maxLength, score, selectedClasses, submitHighscoreMutation]);

  const moveSnake = useCallback(() => {
    const currentSnake = snakeRef.current;
    const currentDirection = directionRef.current;
    if (currentSnake.length === 0) return;

    const head = currentSnake[0];
    const nextHead: Position = {
      x: head.x + (currentDirection === 'RIGHT' ? 1 : currentDirection === 'LEFT' ? -1 : 0),
      y: head.y + (currentDirection === 'DOWN' ? 1 : currentDirection === 'UP' ? -1 : 0)
    };

    // Prüfe Grenzen - die Schlange darf nicht außerhalb des Grids sein
    if (nextHead.x < 0 || nextHead.x >= GRID_COLUMNS || nextHead.y < 0 || nextHead.y >= GRID_ROWS) {
      finishGame('collision');
      return;
    }

    // Prüfe Selbstkollision - die Schlange darf nicht in sich selbst beißen
    if (currentSnake.some(segment => segment.x === nextHead.x && segment.y === nextHead.y)) {
      finishGame('collision');
      return;
    }

    const newSnake = [nextHead, ...currentSnake];
    let grew = false;

    const currentFoods = foodItemsRef.current;
    const consumedFood = currentFoods.find(item => item.position.x === nextHead.x && item.position.y === nextHead.y);

    if (consumedFood) {
      if (consumedFood.id !== targetFoodId) {
        finishGame('wrongFood');
        return;
      }

      grew = true;
      setScore(prev => prev + 1);
      setMaxLength(prev => Math.max(prev, newSnake.length));

      const remainingFoods = currentFoods.filter(item => item.id !== consumedFood.id);
      
      // Filtere Schüler aus, die bereits als Food-Items existieren
      const usedStudentIds = new Set(remainingFoods.map(item => item.student.sophomorixUnid));
      const availableStudents = studentPool.filter(student => !usedStudentIds.has(student.sophomorixUnid));

      const poolForReplacement = availableStudents.length > 0 ? availableStudents : studentPool;
      const replacement = buildFoodItem(newSnake, remainingFoods, poolForReplacement);
      const nextFoods = replacement ? [...remainingFoods, replacement] : remainingFoods;
      setFoodItems(nextFoods);
      foodItemsRef.current = nextFoods;
      pickNewTarget(nextFoods);
    }

    if (!grew) {
      newSnake.pop();
    }

    snakeRef.current = newSnake;
    setSnake(newSnake);
  }, [buildFoodItem, finishGame, pickNewTarget, studentPool, targetFoodId]);

  useEffect(() => {
    if (gamePhase !== 'playing') return;
    
    const interval = window.setInterval(() => {
      moveSnake();
    }, TICK_INTERVAL);
    
    return () => window.clearInterval(interval);
  }, [gamePhase, moveSnake]);

  const prepareFoodSet = useCallback((initialSnake: Position[], pool: SnakeStudent[]) => {
    let items: FoodItem[] = [];
    for (let i = 0; i < FOOD_TARGET_COUNT; i += 1) {
      const item = buildFoodItem([...initialSnake], items, pool);
      if (item) {
        items = [...items, item];
      }
    }
    setFoodItems(items);
    foodItemsRef.current = items;
    pickNewTarget(items);
  }, [buildFoodItem, pickNewTarget]);

  const handleStartGame = useCallback(async () => {
    if (selectedClasses.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Klasse aus');
      return;
    }

    try {
      setGamePhase('loading');
      const response = await api.get('/students', {
        params: {
          includePhotos: 'true'
        }
      });
      const rawStudents: StudentRecord[] = response.data?.data || [];
      const filtered = rawStudents.filter(student => normalizedSelectedClasses.includes(student.Klasse) && student.Vorname && student.Name);

      const unique = Array.from(new Map(filtered.map(student => [student.sophomorixUnid, student])).values());

      if (unique.length < Math.max(FOOD_TARGET_COUNT * 2, FOOD_TARGET_COUNT + snake.length)) {
        toast.error('Nicht genügend Schüler für die ausgewählten Klassen vorhanden');
        setGamePhase('setup');
        return;
      }

      // Load student photos from DATA_PATH in batches to avoid the 100 photo limit
      const studentIds = unique.map(student => student.sophomorixUnid);
      const batchSize = 50; // Load photos in batches of 50
      const allPhotos: Record<string, string | null> = {};
      
      for (let i = 0; i < studentIds.length; i += batchSize) {
        const batch = studentIds.slice(i, i + batchSize);
        const batchPhotos = await loadStudentPhotos(batch);
        Object.assign(allPhotos, batchPhotos);
      }
      setStudentPhotos(allPhotos);

      // Filter students that have photos
      const studentsWithPhotos = unique.filter(student => hasPhoto(allPhotos[student.sophomorixUnid]));

      if (studentsWithPhotos.length < Math.max(FOOD_TARGET_COUNT * 2, FOOD_TARGET_COUNT + snake.length)) {
        toast.error('Nicht genügend Schülerfotos für die ausgewählten Klassen vorhanden');
        setGamePhase('setup');
        return;
      }

      const enhanced = studentsWithPhotos.map(student => ({
        ...student,
        fullName: `${student.Vorname} ${student.Name}`
      }));

      setStudentPool(enhanced);
      resetGameState();
      prepareFoodSet(INITIAL_SNAKE, enhanced);
      startTimestampRef.current = Date.now();
      setGamePhase('ready');
      
      // Starte das Spiel nach 2 Sekunden
      setTimeout(() => {
        setGamePhase('playing');
      }, 2000);
    } catch (error) {
      console.error('Fehler beim Laden der Schüler für Snake:', error);
      toast.error('Schüler konnten nicht geladen werden');
      setGamePhase('setup');
    }
  }, [prepareFoodSet, resetGameState, selectedClasses]);

  const handleSelectAllClasses = () => {
    setSelectedClasses(classes.map(item => item.Klasse));
    classesInitializedRef.current = true;
  };

  const handleClearClasses = () => {
    setSelectedClasses([]);
    classesInitializedRef.current = true;
  };

  const toggleClass = (klasse: string) => {
    setSelectedClasses(prev => prev.includes(klasse) ? prev.filter(item => item !== klasse) : [...prev, klasse]);
  };

  const canStart = selectedClasses.length > 0 && !classesLoading;

  const targetStudent = foodItems.find(item => item.id === targetFoodId)?.student;

  const reasonText = gameOverReason === 'collision'
    ? 'Die Schlange ist kollidiert.'
    : 'Falsches Foto erwischt.';

  const computeTileSize = useCallback(() => {
    const wrapper = boardWrapperRef.current;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Für 1280x720 Bildschirmgröße optimiert
    const isSmallScreen = viewportWidth <= 1280 || viewportHeight <= 720;
    
    let availableWidth, availableHeight;
    
    if (isSmallScreen) {
      // Bei kleineren Bildschirmen mehr Platz für das Spielfeld reservieren (12x8 Grid, 50% größere Kacheln)
      availableWidth = Math.max(702, Math.min(viewportWidth - 80, 1200));
      availableHeight = Math.max(468, Math.min(viewportHeight - 200, 640));
    } else {
      // Bei größeren Bildschirmen normale Berechnung
      const wrapperWidth = wrapper?.clientWidth ?? viewportWidth;
      const offsetTop = wrapper ? wrapper.getBoundingClientRect().top : 0;
      
      availableWidth = Math.max(702, Math.min(wrapperWidth - 24, viewportWidth - 48));
      availableHeight = Math.max(468, viewportHeight - offsetTop - 180);
    }

    const sizeFromWidth = Math.floor(availableWidth / GRID_COLUMNS);
    const sizeFromHeight = Math.floor(availableHeight / GRID_ROWS);
    const baseSize = Math.min(sizeFromWidth, sizeFromHeight);
    
    // Dynamische Mindestgröße basierend auf Bildschirmgröße (50% größer)
    const dynamicMinSize = isSmallScreen ? 58 : MIN_TILE_SIZE;
    const computed = Math.max(dynamicMinSize, baseSize);
    
    setTileSize(computed);
  }, []);

  useEffect(() => {
    computeTileSize();
    window.addEventListener('resize', computeTileSize);
    
    // Zusätzliche Berechnung nach einem kurzen Delay für bessere Initialisierung
    const timeoutId = setTimeout(computeTileSize, 100);
    
    return () => {
      window.removeEventListener('resize', computeTileSize);
      clearTimeout(timeoutId);
    };
  }, [computeTileSize]);

  useEffect(() => {
    if (gamePhase === 'playing') {
      computeTileSize();
    }
  }, [computeTileSize, gamePhase]);

  return (
    <div className="w-full mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/namen-lernen" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Namen lernen · Snake</h1>
      </div>

      <div className="space-y-8">
        <div className="space-y-6">
          {gamePhase === 'setup' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Klassen auswählen</h2>
                  <p className="text-gray-500 text-sm">Wählen Sie die Klassen, deren Namen im Snake-Spiel geübt werden sollen.</p>
                </div>
                <Users className="w-8 h-8 text-primary-500" />
              </div>

              {classesLoading ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Klassen werden geladen…
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllClasses}
                      className="px-3 py-1.5 text-sm rounded-full bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                    >
                      Alle auswählen
                    </button>
                    <button
                      type="button"
                      onClick={handleClearClasses}
                      className="px-3 py-1.5 text-sm rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                      Auswahl leeren
                    </button>
                    {selectedClasses.length > 0 && (
                      <span className="px-3 py-1.5 text-sm rounded-full bg-green-100 text-green-700">
                        {selectedClasses.length} Klasse{selectedClasses.length === 1 ? '' : 'n'} ausgewählt
                      </span>
                    )}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                    {classes.map(klasse => {
                      const isSelected = selectedClasses.includes(klasse.Klasse);
                      return (
                        <label
                          key={klasse.Klasse}
                          className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer transition-all ${
                            isSelected ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="rounded text-primary-600 focus:ring-primary-500"
                            checked={isSelected}
                            onChange={() => toggleClass(klasse.Klasse)}
                          />
                          <span className="font-medium">{klasse.Klasse}</span>
                          {klasse.anzahl && (
                            <span className="text-xs text-gray-500">({klasse.anzahl})</span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleStartGame}
                      disabled={!canStart}
                      className={`w-full md:w-auto px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 ${
                        canStart
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Spiel starten
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {gamePhase === 'loading' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
              <p className="text-gray-600">Spiel wird vorbereitet…</p>
            </div>
          )}

          {gamePhase === 'ready' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center justify-center gap-4">
              <div className="text-6xl">🐍</div>
              <h2 className="text-2xl font-bold text-gray-900">Spiel startet in 2 Sekunden...</h2>
              <p className="text-gray-600">Bereiten Sie sich vor und steuern Sie die Schlange mit den Pfeiltasten!</p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-primary-600 rounded-full animate-pulse"></div>
                <span>Bereit für den Start!</span>
              </div>
            </div>
          )}

          {gamePhase === 'playing' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-500">Aktuelle Aufgabe</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {targetStudent ? (
                        <>Gehe zu <span className="text-primary-600">{targetStudent.fullName}</span></>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Punkte</div>
                    <div className="text-2xl font-bold text-gray-900">{score}</div>
                    <div className="text-xs text-gray-500">Längste Schlange: {maxLength}</div>
                  </div>
                </div>

                <div ref={boardWrapperRef} className="relative bg-gray-100 border border-gray-200 rounded-2xl overflow-hidden flex items-center justify-center min-h-[300px]">
                  <div
                    className="grid"
                    style={{
                      gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${tileSize}px)`,
                      gridTemplateRows: `repeat(${GRID_ROWS}, ${tileSize}px)`,
                      width: GRID_COLUMNS * tileSize,
                      height: GRID_ROWS * tileSize
                    }}
                  >
                    {Array.from({ length: GRID_COLUMNS * GRID_ROWS }).map((_, index) => {
                      const x = index % GRID_COLUMNS;
                      const y = Math.floor(index / GRID_COLUMNS);
                      const isSnake = snake.some(segment => segment.x === x && segment.y === y);
                      const isHead = snake[0]?.x === x && snake[0]?.y === y;
                      const food = foodItems.find(item => item.position.x === x && item.position.y === y);

                      return (
                        <div
                          key={`${x}-${y}`}
                          className={`border border-white/40 ${isHead ? 'bg-primary-600 shadow-inner' : isSnake ? 'bg-primary-400 shadow-inner' : 'bg-transparent'}`}
                          style={{ position: 'relative', width: tileSize, height: tileSize }}
                        >
                          {food && (
                            <div className={`absolute inset-1 rounded-lg overflow-hidden border ${food.id === targetFoodId ? 'border-primary-500' : 'border-transparent'}`}>
                              {hasPhoto(studentPhotos[food.student.sophomorixUnid]) ? (
                                <img
                                  src={createPhotoDataUrl(studentPhotos[food.student.sophomorixUnid]!)}
                                  alt={food.student.fullName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                  <Users className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-sm text-gray-600">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-primary-500" /> Steuerung</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Pfeiltasten steuern die Schlange – direkte Kehrtwendungen sind nicht erlaubt.</li>
                  <li>Die Schlange bleibt dauerhaft in Bewegung.</li>
                  <li>Nur das angezeigte Foto fressen – falsche Fotos beenden das Spiel.</li>
                </ul>
              </div>
            </div>
          )}

          {gamePhase === 'paused' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center justify-center gap-4">
              <div className="text-6xl">⏸️</div>
              <h2 className="text-2xl font-bold text-gray-900">Spiel pausiert</h2>
              <p className="text-gray-600">Drücken Sie die Leertaste, um fortzufahren</p>
              <div className="text-sm text-gray-500">
                <div>Punkte: {score}</div>
                <div>Längste Schlange: {maxLength}</div>
              </div>
            </div>
          )}

          {gamePhase === 'finished' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-6">
              <div className="flex items-center gap-3">
                <Trophy className="w-10 h-10 text-amber-500" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Spiel beendet</h2>
                  <p className="text-gray-500">{reasonText}</p>
                </div>
              </div>

              <dl className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="bg-primary-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-primary-600">Punkte</dt>
                  <dd className="text-2xl font-bold text-primary-700">{score}</dd>
                </div>
                <div className="bg-green-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-green-600">Max. Länge</dt>
                  <dd className="text-2xl font-bold text-green-700">{maxLength}</dd>
                </div>
                <div className="bg-blue-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-blue-600">Spielzeit</dt>
                  <dd className="text-lg font-semibold text-blue-700">{lastDuration}s</dd>
                </div>
                <div className="bg-purple-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-purple-600">Gespielte Klassen</dt>
                  <dd className="text-lg font-semibold text-purple-700">{selectedClasses.join(', ') || '—'}</dd>
                </div>
              </dl>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setGamePhase('setup')}
                  className="px-5 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zurück zur Auswahl
                </button>
                <button
                  type="button"
                  onClick={handleStartGame}
                  className="px-5 py-3 rounded-xl bg-primary-600 text-white hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Noch einmal spielen
                </button>
              </div>

              {isSubmittingScore && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Highscore wird aktualisiert…
                </div>
              )}
            </div>
          )}
        </div>

        {/* Highscores und Spielregeln nach unten verschoben */}
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Highscores</h2>
              <Award className="w-6 h-6 text-amber-500" />
            </div>

            {highscoreLoading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                Highscores werden geladen…
              </div>
            ) : mergedHighscores.length === 0 ? (
              <p className="text-gray-500 text-sm">Noch keine Einträge vorhanden. Spielen Sie eine Runde und setzen Sie den ersten Rekord!</p>
            ) : (
              <ul className="space-y-2">
                {mergedHighscores.slice(0, 5).map((entry, index) => {
                  const isCurrentUser = entry.username && user?.username && entry.username.toLowerCase() === user.username.toLowerCase();
                  return (
                    <li
                      key={`${entry.username}-${getHighscoreTimestamp(entry)}-${index}`}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                        isCurrentUser ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-gray-200'
                      }`}
                    >
                      <div>
                        <div className="text-sm font-semibold">{index + 1}. {entry.username || 'Unbekannt'}</div>
                        <div className="text-xs text-gray-500">
                          {getHighscoreTimestamp(entry) ? new Date(getHighscoreTimestamp(entry)!).toLocaleDateString('de-DE') : '—'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">{entry.score ?? 0} Punkte</div>
                        {entry.maxLength && (
                          <div className="text-xs text-gray-500">Länge {entry.maxLength}</div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {userBestScore && (
              <div className="mt-4 rounded-xl bg-green-50 text-green-700 px-4 py-3 text-sm">
                <div className="font-semibold">Ihr persönlicher Rekord</div>
                <div className="flex items-center justify-between">
                  <span>{userBestScore.score ?? 0} Punkte</span>
                  {userBestScore.maxLength && (
                    <span>Länge {userBestScore.maxLength}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4 text-sm text-gray-600">
            <h3 className="text-lg font-semibold text-gray-900">Spielregeln</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Steuern Sie die Schlange mit den Pfeiltasten zum angezeigten Schülerfoto.</li>
              <li>Drücken Sie die Leertaste zum Pausieren/Fortsetzen.</li>
              <li>Jedes richtige Foto verlängert die Schlange und erhöht den Punktestand.</li>
              <li>Falsche Fotos oder Kollisionen beenden das Spiel sofort.</li>
              <li>Versuchen Sie, die längste Schlange und die höchste Punktzahl zu erreichen.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
